import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedLogoProps {
  className?: string;
}

export const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ className }) => {
  const pathRef = useRef<SVGPathElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const tipGlowRef = useRef<SVGCircleElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    const path = pathRef.current;
    const tip = tipRef.current;
    const tipGlow = tipGlowRef.current;

    if (!path || !tip || !tipGlow) return;

    // We only get the length if it's available
    let length = 20000;
    try {
      if (typeof path.getTotalLength === 'function') {
        length = path.getTotalLength() || 20000;
      }
    } catch (e) {}

    const DRAW_MS = 2600;  // duration of the drawing
    const FILL_MS = 550;   // solid fill after drawing
    const HOLD_MS = 700;   // full logo hold time
    const ERASE_MS = 550;  // fade out time
    const GAP_MS = 250;    // gap before repeating

    let startTime = 0;

    const clearAllTimeouts = () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };

    const addTimeout = (cb: () => void, ms: number) => {
      const t = setTimeout(cb, ms);
      timeoutsRef.current.push(t);
    };

    function resetToStart() {
      if (!path || !tip || !tipGlow) return;
      path.style.transition = 'none';
      path.style.strokeDasharray = `${length} ${length}`;
      path.style.strokeDashoffset = `${length}`;
      path.style.fillOpacity = '0';
      // force reflow
      path.getBoundingClientRect();

      tip.style.transition = 'none';
      tipGlow.style.transition = 'none';
      tip.style.opacity = '0';
      tipGlow.style.opacity = '0';
    }

    function moveTip(elapsed: number) {
      if (!path || !tip || !tipGlow) return;
      try {
        const t = Math.min(elapsed / DRAW_MS, 1);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const p = path.getPointAtLength ? path.getPointAtLength(eased * length) : { x: 0, y: 0 };
        if (p && typeof p.x === 'number' && typeof p.y === 'number') {
          tip.setAttribute('cx', p.x.toString());
          tip.setAttribute('cy', p.y.toString());
          tipGlow.setAttribute('cx', p.x.toString());
          tipGlow.setAttribute('cy', p.y.toString());
        }

        if (t < 1) {
          rafIdRef.current = requestAnimationFrame((ts) => moveTip(ts - startTime));
        } else {
          tip.style.transition = 'opacity .3s ease';
          tipGlow.style.transition = 'opacity .3s ease';
          tip.style.opacity = '0';
          tipGlow.style.opacity = '0';
        }
      } catch (e) {
        // Fallback silencioso se getPointAtLength não for suportado no dispositivo
      }
    }

    function drawStroke() {
      if (!path || !tip || !tipGlow) return;
      path.style.transition = `stroke-dashoffset ${DRAW_MS}ms cubic-bezier(.65,0,.35,1), fill-opacity ${FILL_MS}ms ease ${DRAW_MS + 60}ms`;
      rafIdRef.current = requestAnimationFrame(() => {
        path.style.strokeDashoffset = '0';
        path.style.fillOpacity = '1';

        tip.style.transition = 'opacity .2s ease';
        tipGlow.style.transition = 'opacity .2s ease';
        tip.style.opacity = '1';
        tipGlow.style.opacity = '0.55';

        startTime = performance.now();
        rafIdRef.current = requestAnimationFrame((ts) => moveTip(ts - startTime));
      });
    }

    function eraseStroke() {
      if (!path) return;
      path.style.transition = `fill-opacity ${ERASE_MS * 0.5}ms ease, stroke-dashoffset ${ERASE_MS}ms cubic-bezier(.65,0,.35,1)`;
      rafIdRef.current = requestAnimationFrame(() => {
        path.style.fillOpacity = '0';
        path.style.strokeDashoffset = `-${length}`;
      });
    }

    function loop() {
      resetToStart();
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = requestAnimationFrame(drawStroke);
      });

      addTimeout(eraseStroke, DRAW_MS + FILL_MS + HOLD_MS);
      addTimeout(loop, DRAW_MS + FILL_MS + HOLD_MS + ERASE_MS + GAP_MS);
    }

    // Delay a tiny bit so length calculation is accurate in DOM
    addTimeout(() => {
      loop();
    }, 50);

    return () => {
      clearAllTimeouts();
    };
  }, []);

  return (
    <div className={cn("relative flex items-center justify-center w-full aspect-square", className)}>
      <svg 
        viewBox="0 0 1080 1080" 
        className="w-full h-full overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform="translate(0,1080) scale(0.1,-0.1)">
          <path 
            ref={pathRef}
            d="M4615 9534 c-49 -17 -93 -60 -105 -103 -5 -20 -10 -160 -10 -311 0 -302 17 -1279 24 -1430 3 -52 8 -183 11 -290 8 -267 39 -821 66 -1165 24 -321 26 -360 20 -366 -3 -4 -46 -19 -96 -34 -252 -75 -758 -236 -935 -296 -63 -22 -119 -38 -125 -36 -7 3 -8 91 -4 268 6 257 9 324 29 669 22 375 87 1064 130 1380 24 177 24 184 1 227 -32 64 -73 88 -151 88 -74 0 -118 -23 -146 -77 -14 -27 -48 -260 -73 -503 -50 -480 -66 -661 -81 -895 -6 -85 -12 -173 -15 -195 -6 -62 -35 -783 -38 -944 l-2 -143 -125 -44 c-69 -24 -172 -61 -230 -81 -245 -85 -767 -284 -789 -301 -100 -75 -90 -221 20 -280 58 -31 100 -26 254 34 66 25 167 63 225 84 58 21 157 57 220 80 186 68 421 150 429 150 4 0 13 -100 19 -222 7 -123 16 -259 21 -303 6 -44 15 -125 21 -180 26 -224 83 -500 138 -670 67 -203 97 -254 167 -280 137 -53 265 84 211 225 -63 162 -108 315 -136 465 -35 187 -64 408 -75 575 -4 52 -8 109 -10 125 -2 17 -7 109 -11 206 l-7 175 29 12 c60 25 669 223 913 297 277 84 271 84 271 13 0 -20 4 -70 10 -110 5 -40 17 -131 25 -203 123 -1019 295 -1712 529 -2139 76 -138 104 -161 195 -161 75 0 119 23 148 78 29 56 23 124 -19 192 -223 372 -397 1059 -517 2050 -41 336 -54 456 -50 459 2 2 67 24 144 49 292 95 462 161 585 229 214 117 302 245 287 416 -10 104 -64 202 -206 372 -67 80 -154 195 -195 255 -319 476 -546 1081 -686 1830 -22 120 -48 299 -81 568 -8 68 -18 108 -33 129 -41 61 -120 87 -191 62z m313 -2012 c105 -269 290 -617 437 -821 33 -46 98 -129 144 -185 106 -126 161 -204 161 -227 0 -34 -103 -97 -270 -164 -90 -37 -423 -155 -435 -155 -8 0 -15 62 -25 210 -6 91 -17 257 -25 370 -14 200 -25 411 -45 834 -12 251 -12 276 -2 276 4 -1 31 -62 60 -138z M6714 6052 c-23 -15 -138 -108 -255 -208 -462 -391 -680 -549 -861 -625 -65 -28 -88 -32 -205 -38 -112 -5 -136 -9 -160 -27 -44 -33 -67 -81 -67 -141 0 -43 5 -58 30 -90 56 -68 83 -78 219 -77 173 0 316 46 506 161 50 30 92 53 94 51 2 -2 -6 -24 -18 -49 -151 -314 -219 -591 -220 -884 0 -159 12 -245 54 -395 94 -331 310 -611 669 -867 157 -111 365 -222 625 -332 55 -24 114 -49 130 -56 17 -8 55 -25 85 -38 31 -14 94 -40 140 -60 47 -19 110 -46 140 -60 30 -14 100 -44 155 -67 55 -23 127 -55 160 -70 33 -15 98 -43 145 -63 75 -31 117 -49 235 -102 17 -7 104 -46 195 -85 217 -94 232 -101 250 -115 25 -21 178 -75 209 -75 43 0 82 17 119 53 58 56 65 148 15 215 -28 38 -57 53 -293 157 -96 42 -197 87 -225 100 -27 12 -95 42 -150 65 -55 23 -119 51 -142 61 -23 10 -295 129 -605 263 -310 134 -604 263 -653 287 -452 217 -759 513 -868 839 -42 127 -52 195 -52 360 1 201 30 339 120 575 37 99 151 323 218 431 88 141 244 356 367 504 153 183 161 197 163 262 1 65 -20 109 -70 143 -54 36 -141 35 -199 -3z M7097 5195 c-198 -40 -378 -137 -518 -282 -189 -194 -279 -418 -279 -693 0 -276 101 -513 302 -706 141 -135 305 -221 493 -259 129 -25 337 -17 455 18 363 109 608 370 701 747 9 36 14 110 13 215 -1 142 -4 170 -28 250 -109 373 -398 637 -776 710 -92 18 -271 18 -363 0z m353 -342 c118 -32 199 -81 290 -172 133 -135 193 -275 193 -456 0 -183 -59 -323 -192 -456 -117 -118 -216 -167 -379 -190 -151 -22 -304 16 -444 108 -116 76 -205 195 -255 342 -23 66 -27 92 -26 196 0 103 4 131 26 197 86 252 276 406 557 452 42 7 172 -5 230 -21z"
            className="fill-primary stroke-primary outline-none"
            style={{ 
              strokeWidth: 14,
              strokeLinecap: 'round', 
              strokeLinejoin: 'round',
              fillOpacity: 0
            }}
          />
          <circle ref={tipGlowRef} cx="0" cy="0" r="14" className="fill-[#ffb877] opacity-0 blur-[10px]" />
          <circle ref={tipRef} cx="0" cy="0" r="5" className="fill-[#ffb877] opacity-0" />
        </g>
      </svg>
    </div>
  );
};
